import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import HeroPhone from '../features/home/components/HeroPhone';
import EcommerceShowcase from '../features/home/components/EcommerceShowcase';
import CompetitionSearchLite from '../features/home/components/CompetitionSearchLite';
import AIPhoneAssistant from '../features/home/components/AIPhoneAssistant';
import ReviewsMarquee from '../features/home/components/ReviewsMarquee';
import PartnersStrip from '../features/home/components/PartnersStrip';
import IndustriesSection from '../features/home/components/IndustriesSection';
import PrinciplesSection from '../features/home/components/PrinciplesSection';
import FooterSection from '../features/home/components/FooterSection';
import './Home.css';

const HOME_VISIBILITY_SEARCH_DISABLED = true;

const FEATURED_PROJECTS = [
  {
    id: 'wreck-tiki-lounge',
    name: 'The Wreck Tiki Lounge',
    category: 'Jax Beach Bar',
    img: '/images/portfolio/wreck-tiki-lounge.png',
    url: 'https://wrecktikilounge.com'
  },
  {
    id: 'black-pearl-pv',
    name: 'Black Pearl PV',
    category: 'Asian Fusion Restaurant',
    img: '/images/portfolio/black-pearl-pv.png',
    url: 'https://blackpearlpv.com'
  },
  {
    id: 'kiko-ramen-sushi',
    name: 'Kiko Ramen & Sushi',
    category: 'Japanese Restaurant',
    img: '/images/portfolio/kiko-ramen-sushi.png',
    url: 'https://kiko-demo.vercel.app/'
  }
];

const OWNERS = [
  {
    src: '/images/Owner of Wreck tiki bar.png',
    name: 'Fernando',
    role: 'Bar & Event Venue Owner',
    headline: 'Brought more customers for tourists and events',
    stat: '+145%',
    sub: 'online visibility'
  },
  {
    src: '/images/Owner Desiree at bold step consulting.jpg',
    name: 'Desiree',
    role: 'Bold Step Consulting',
    headline: 'Increased leads with online presence',
    stat: '+75%',
    sub: 'new leads'
  },
  {
    src: '/images/owner-barista.jpg',
    name: 'Maria Santos',
    role: 'Barista & Cafe Owner',
    headline: 'Tripled morning rush bookings',
    stat: '+200%',
    sub: 'more bookings'
  },
  {
    src: '/images/owner-clothing-company.jpg',
    name: 'James Anderson',
    role: 'Clothing Company Owner',
    headline: 'Grew customer base significantly',
    stat: '+85%',
    sub: 'new customers'
  },
  {
    src: '/images/owner-bay-badfishslayers.jpg',
    name: 'Baye',
    role: 'Badfish Slayers',
    headline: 'Helped launch his non-profit organization',
    stat: 'Launched',
    sub: 'non-profit brand'
  },
  {
    src: '/images/owner-notary-services.jpg',
    name: 'Patricia Williams',
    role: 'Notary Services',
    headline: 'Doubled client appointments',
    stat: '2X',
    sub: 'appointments'
  },
  {
    src: '/images/owner-potery-shop.jpg',
    name: 'Shaye',
    role: 'Pottery Shop Owner',
    headline: 'Increased workshop enrollment',
    stat: '+120%',
    sub: 'revenue growth'
  },
  {
    src: '/images/owner-sweattreats.jpg',
    name: 'Lisa Martinez',
    role: 'Sweet Treats Bakery',
    headline: 'Online orders up monthly',
    stat: '+$3,500/mo',
    sub: 'online sales'
  },
  {
    src: '/images/two-men-working-in-the-cold.jpg',
    name: 'Sevents Tree Farm',
    role: 'Tree Farm & Seasonal Market',
    headline: 'Booked more seasonal customers online',
    stat: '+72%',
    sub: 'more inquiries'
  }
];

function Home() {
  const owners = OWNERS;

  const [isMobileOwners, setIsMobileOwners] = useState(() => window.innerWidth <= 768);
  const [activeOwnerIndex, setActiveOwnerIndex] = useState(0);
  const [pauseAutoUntil, setPauseAutoUntil] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);
  const [ownersInView, setOwnersInView] = useState(false);
  const ownersSectionRef = useRef(null);

  const advanceOwner = (direction) => {
    setSlideDirection(direction === 'next' ? 1 : -1);
    setActiveOwnerIndex((prevIndex) => {
      if (direction === 'next') {
        return (prevIndex + 1) % owners.length;
      }

      return (prevIndex - 1 + owners.length) % owners.length;
    });
    setPauseAutoUntil(Date.now() + 4500);
  };

  // Preload images for current and next slides
  useEffect(() => {
    if (!isMobileOwners || !ownersInView) return;

    const currentIndex = activeOwnerIndex;
    const nextIndex = (currentIndex + 1) % owners.length;
    const prevIndex = (currentIndex - 1 + owners.length) % owners.length;

    // Preload current, next, and previous images
    [currentIndex, nextIndex, prevIndex].forEach((index) => {
      const img = new Image();
      img.src = process.env.PUBLIC_URL + owners[index].src;
    });
  }, [activeOwnerIndex, isMobileOwners, ownersInView, owners]);

  useEffect(() => {
    if (!isMobileOwners) {
      setOwnersInView(false);
      return undefined;
    }

    const section = ownersSectionRef.current;
    if (!section) {
      return undefined;
    }

    if (!('IntersectionObserver' in window)) {
      setOwnersInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setOwnersInView(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: '240px 0px', threshold: 0.01 }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, [isMobileOwners]);

  useEffect(() => {
    const onResize = () => {
      setIsMobileOwners(window.innerWidth <= 768);
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (!isMobileOwners) {
      setActiveOwnerIndex(0);
      return undefined;
    }

    const interval = window.setInterval(() => {
      if (Date.now() < pauseAutoUntil) {
        return;
      }

      setSlideDirection(1);
      setActiveOwnerIndex((prevIndex) => (prevIndex + 1) % owners.length);
    }, 3200);

    return () => {
      window.clearInterval(interval);
    };
  }, [isMobileOwners, owners.length, pauseAutoUntil]);

  return (
    <main className="home">
      <Helmet>
        <title>Versa Launch - AI-Powered Local Business Growth Platform</title>
        <meta name="description" content="Launch your local service business to the next level with AI phone assistant, local SEO, lead generation, and automation. Get more visibility, more calls, more jobs." />
        <meta name="keywords" content="local business growth, AI phone assistant, lead generation, local SEO, plumbing software, HVAC software, service business tools" />
        <link rel="canonical" href="https://versalaunch.com/" />
        <meta property="og:title" content="Versa Launch - Launch Your Business to the Next Level" />
        <meta property="og:description" content="Grow your local service business with AI-powered automation, lead generation, and 24/7 customer service." />
        <meta name="twitter:title" content="Versa Launch - Local Business Growth Platform" />
        <meta name="twitter:description" content="AI-powered growth tools for local service businesses. More visibility, more leads, more revenue." />
      </Helmet>

      {/* Hero Phone Section - Shows animated ranking */}
      <HeroPhone />

      {/* Partner / integration logos - right under the hero phone card */}
      <PartnersStrip />

      {/* Interactive Demo - Report Generator */}
      {!HOME_VISIBILITY_SEARCH_DISABLED && <CompetitionSearchLite />}

      {/* Owners Marquee */}
      <section className="owners-section" ref={ownersSectionRef}>
        <div className="container">
          <motion.h2
            className="section-title"
            initial={false}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, amount: 0.3 }}
            style={{ willChange: 'opacity', backfaceVisibility: 'hidden' }}
          >
            Trusted by <span className="home-gold-text">100+</span> local businesses
          </motion.h2>
          <motion.p
            className="section-subtitle"
            initial={false}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true, amount: 0.3 }}
            style={{ willChange: 'opacity', backfaceVisibility: 'hidden' }}
          >
            See how we've helped local owners grow their presence.
          </motion.p>

          {isMobileOwners ? (
            <div className="owners-mobile-carousel">
              <div className="owners-mobile-card-wrapper">
                <AnimatePresence mode="sync" custom={slideDirection}>
                  <motion.div
                    key={activeOwnerIndex}
                    className="owner-card owner-card-mobile"
                    custom={slideDirection}
                    variants={{
                      enter: (dir) => ({ opacity: 0, scale: 0.88, rotateZ: dir > 0 ? 5 : -5 }),
                      center: { opacity: 1, scale: 1, rotateZ: -2 },
                      exit: (dir) => ({ opacity: 0, scale: 1.06, rotateZ: dir > 0 ? -5 : 5 })
                    }}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.42, ease: [0.4, 0, 0.2, 1] }}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      willChange: 'transform, opacity',
                      WebkitBackfaceVisibility: 'hidden',
                      backfaceVisibility: 'hidden'
                    }}
                    drag="x"
                    dragDirectionLock
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.15}
                    onDragEnd={(_, info) => {
                      const passedRight = info.offset.x > 55 || info.velocity.x > 420;
                      const passedLeft = info.offset.x < -55 || info.velocity.x < -420;

                      if (passedRight) {
                        advanceOwner('prev');
                        return;
                      }

                      if (passedLeft) {
                        advanceOwner('next');
                      }
                    }}
                    whileTap={{ cursor: 'grabbing' }}
                  >
                    <div className="owner-image">
                      <img src={process.env.PUBLIC_URL + owners[activeOwnerIndex].src} alt={owners[activeOwnerIndex].role} loading="lazy" />
                    </div>
                    <div className="owner-overlay">
                      <div className="owner-top">
                        <div className="owner-name">{owners[activeOwnerIndex].name}</div>
                        <div className="owner-role">{owners[activeOwnerIndex].role}</div>
                      </div>
                      <div className="owner-bottom">
                        <div className="owner-headline">{owners[activeOwnerIndex].headline}</div>
                        <div className="owner-stat">
                          <span>{owners[activeOwnerIndex].stat}</span>
                          <small>{owners[activeOwnerIndex].sub}</small>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="owners-mobile-dots" aria-hidden="true">
                {owners.map((item, index) => (
                  <span
                    key={`${item.src}-dot-${index}`}
                    className={`owners-mobile-dot${index === activeOwnerIndex ? ' is-active' : ''}`}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="owners-marquee">
              <motion.div
                className="owners-track"
                initial={false}
                animate={{ opacity: 1 }}
              >
                {owners.map((item) => (
                  <motion.div key={item.src} className="owner-card" whileHover={{ y: -8, scale: 1.01 }}>
                    <div className="owner-image">
                      <img src={process.env.PUBLIC_URL + item.src} alt={item.role} loading="lazy" />
                    </div>
                    <div className="owner-overlay">
                      <div className="owner-top">
                        <div className="owner-name">{item.name}</div>
                        <div className="owner-role">{item.role}</div>
                      </div>
                      <div className="owner-bottom">
                        <div className="owner-headline">{item.headline}</div>
                        <div className="owner-stat">
                          <span>{item.stat}</span>
                          <small>{item.sub}</small>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {owners.map((item, index) => (
                  <motion.div key={`${item.src}-dup-${index}`} className="owner-card" whileHover={{ y: -8, scale: 1.01 }}>
                    <div className="owner-image">
                      <img src={process.env.PUBLIC_URL + item.src} alt={item.role} loading="lazy" />
                    </div>
                    <div className="owner-overlay">
                      <div className="owner-top">
                        <div className="owner-name">{item.name}</div>
                        <div className="owner-role">{item.role}</div>
                      </div>
                      <div className="owner-bottom">
                        <div className="owner-headline">{item.headline}</div>
                        <div className="owner-stat">
                          <span>{item.stat}</span>
                          <small>{item.sub}</small>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          )}
        </div>
      </section>

      {/* AI Phone Assistant */}
      <AIPhoneAssistant />

      {/* Google Reviews - auto-scrolling wall */}
      <ReviewsMarquee />

      {/* Versatile / Industries Section */}
      <IndustriesSection />

      {/* Portfolio Strip */}
      <section className="home-portfolio-strip">
        <div className="container">
          <motion.div
            className="home-portfolio-header"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="home-portfolio-heading">
              Our Work<span className="home-gold-text">.</span>
            </h2>
            <p className="home-portfolio-subheading">Real sites built for real local businesses.</p>
          </motion.div>

          <div className="home-portfolio-grid">
            {FEATURED_PROJECTS.map((project, idx) => (
              <motion.a
                key={project.id}
                href={project.url}
                target="_blank"
                rel="noreferrer"
                className="home-pf-card"
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: idx * 0.1, ease: 'easeOut' }}
                viewport={{ once: true, amount: 0.15 }}
              >
                <div className="home-pf-frame">
                  <img src={project.img} alt={`${project.name} website preview`} loading="lazy" />
                  <div className="home-pf-overlay">
                    <span className="home-pf-category">{project.category}</span>
                    <span className="home-pf-name">{project.name}</span>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>

          <motion.div
            className="home-portfolio-cta-row"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3, ease: 'easeOut' }}
            viewport={{ once: true, amount: 0.5 }}
          >
            <Link to="/portfolio" className="home-portfolio-cta-btn">
              View our full portfolio <ArrowUpRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Principles Section */}
      <PrinciplesSection />

      {/* Online Store Showcase - device pops out of the section */}
      <EcommerceShowcase />

      {/* Restaurant Growth CTA Section */}
      <section className="restaurant-growth-cta" style={{ backgroundImage: "url('/images/Getmoreonlinebusiness.jpg')" }}>
        <div className="restaurant-cta-overlay">
          <div className="restaurant-cta-content">
            <h2>The simplest way to grow <span className="home-black-text">your business</span>.</h2>
            <div className="restaurant-cta-buttons">
              <Link to="/plans" style={{ textDecoration: 'none' }}>
                <motion.button 
                  className="cta-btn cta-btn-primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>Get started</span>
                  <span className="arrow">↗</span>
                </motion.button>
              </Link>
              <Link to="/resources/getting-started" style={{ textDecoration: 'none' }}>
                <motion.button 
                  className="cta-btn cta-btn-secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>See how it works</span>
                  <span className="arrow">↗</span>
                </motion.button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <FooterSection />
    </main>
  );
}

export default Home;
